import { useEffect, useMemo, useState } from 'react';
import { useUnit } from 'effector-react';
import debounce from 'lodash.debounce';
import { useParams } from '@nx-frontend/shared/hooks/router-proxy';
import { $app } from '@nx-frontend/shared/store/app';
import { $currentOrganization } from '@nx-frontend/shared/store/current-organization';
import { $currentWorkspace } from '@nx-frontend/shared/store/current-workspace';
import { initExpandedMenuItemsFx } from '@nx-frontend/shared/store/feature-menu-tree';
import { fetchItemsInOpenendFoldersFx } from '@nx-frontend/shared/store/internal-space-menu';
import { fetchMembersEvent } from '@nx-frontend/shared/store/members';
import { $note } from '@nx-frontend/shared/store/note';
import { startFetchUnreadMessagesEvent } from '@nx-frontend/shared/store/org-chat';
import { $portalSettings } from '@nx-frontend/shared/store/portal';
import { $sidebar, setSidebarIsLoadedEvent } from '@nx-frontend/shared/store/sidebar';
import { $user } from '@nx-frontend/shared/store/user';
import { fetchWorkspaceMembersEvent } from '@nx-frontend/shared/store/workspace-members-webnotes';
import { MenuItemType } from '@nx-frontend/shared/types';
import { scrollToSidebarMenuItem } from '@nx-frontend/shared/utils';
import { createAbortController, stopAbortController } from '../page-abort-controller';
import { useExpandOnFirstLoad } from './use-expand-on-first-load';
import { useSelectCurrentItem } from './use-select-current-item';
import { useSocket } from './use-socket';

export const useInitialLoad = () => {
  const [
    initExpandedMenuItems,
    fetchMembers,
    startFetchUnreadMessages,
    fetchWorkspaceMembers,
    fetchItemsInOpenendFolders,
    setSidebarIsLoaded,
  ] = useUnit([
    initExpandedMenuItemsFx,
    fetchMembersEvent,
    startFetchUnreadMessagesEvent,
    fetchWorkspaceMembersEvent,
    fetchItemsInOpenendFoldersFx,
    setSidebarIsLoadedEvent,
  ]);

  const [currentOrganization, currentWorkspace, note, app, portalSettings, user, sidebar] = useUnit([
    $currentOrganization,
    $currentWorkspace,
    $note,
    $app,
    $portalSettings,
    $user,
    $sidebar,
  ]);

  const [firstExpandedLoad, setFirstExpandedLoad] = useState(false);
  const params = useParams();
  const slug = useMemo(() => params?.slug || [], [params?.slug]) as string[];
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [pathWorkspaceId, pageType, itemIdEncoded] = slug;
  const itemId = decodeURIComponent(itemIdEncoded);

  const workspaceId = currentWorkspace.globalId;

  useEffect(() => {
    fetchMembers({ orgId: currentOrganization.globalId });
  }, [fetchMembers, currentOrganization.globalId]);

  useEffect(() => {
    fetchWorkspaceMembers(workspaceId);
  }, [fetchWorkspaceMembers, workspaceId]);

  useExpandOnFirstLoad({
    pathWorkspaceId: workspaceId,
    portalId: portalSettings.id,
    userId: user.id,
  });

  const currentStoreNote = useMemo(() => {
    return pageType === 'page' ? note : null;
  }, [note, pageType]);

  // fetch items for expanded folders
  const fetchExpandedItems = useMemo(
    () => async () => {
      return new Promise((resolve, reject) => {
        stopAbortController();
        initExpandedMenuItems().then(async (expandedItemsIds) => {
          // only opened folders in current workspace
          if (expandedItemsIds?.length) {
            const pagesAbortController = createAbortController();
            try {
              await fetchItemsInOpenendFolders({
                sessionId: app.sessionId,
                workspaceId: currentWorkspace.globalId,
                parentKey: expandedItemsIds,
                abortController: pagesAbortController,
              });
              setSidebarIsLoaded(true);
              if (currentStoreNote) {
                const isPortalNote = currentStoreNote.portalMenuItem && currentStoreNote.isSharedForPortal;
                let id = `${
                  currentStoreNote.type === 'note' ? MenuItemType.NOTE : MenuItemType.NOTES_FOLDER
                }#${itemId}`;
                if (isPortalNote && currentStoreNote.portalMenuItem) {
                  id = currentStoreNote.portalMenuItem.id;
                }
                scrollToSidebarMenuItem(id);
              }
            } catch (err) {
              console.log(err);
            }
          }
          resolve(true);
        });
      });
    },
    [
      app.sessionId,
      currentStoreNote,
      currentWorkspace.globalId,
      fetchItemsInOpenendFolders,
      itemId,
      initExpandedMenuItems,
      setSidebarIsLoaded,
    ]
  );

  const setExpandedMenuItemsDebunce = useMemo(
    () => debounce(fetchExpandedItems, 10, { trailing: true }),
    [fetchExpandedItems]
  );

  useEffect(() => {
    if (!firstExpandedLoad) {
      setExpandedMenuItemsDebunce();
      setFirstExpandedLoad(true);
    }
  }, [setExpandedMenuItemsDebunce, firstExpandedLoad, setFirstExpandedLoad]);

  useSelectCurrentItem({
    pageType,
    itemId,
    currentStoreNote,
    fetchExpandedItems,
    doNotScrollToNote: sidebar.scrollRestricted,
  });

  useSocket();

  useEffect(() => {
    const fetchUnreadMessages = async () => {
      startFetchUnreadMessages();
      const intervalId = setInterval(async () => {
        startFetchUnreadMessages();
      }, 60000);
      return () => {
        clearInterval(intervalId);
      };
    };
    fetchUnreadMessages();
  }, [currentOrganization, startFetchUnreadMessages]);
};
